import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ENV } from '../../../config/env';
import { BrandKitState } from '../BrandKitCreator';

interface BusinessName {
  name: string;
  explanation: string;
  tagline: string;
}

interface Props {
  brandKit: BrandKitState;
  updateBrandKit: (updates: Partial<BrandKitState>) => void;
  onNext: () => void;
}

const BusinessNameTab: React.FC<Props> = ({ brandKit, updateBrandKit, onNext }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [industry, setIndustry] = useState(brandKit.industry || '');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [description, setDescription] = useState(brandKit.description || '');
  const [businessNames, setBusinessNames] = useState<BusinessName[]>([]);
  const [selectedName, setSelectedName] = useState<BusinessName | null>(null);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!industry || keywords.length === 0 || !description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/business-name`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            industry, 
            keywords, 
            description 
          }),
        }
      );
      
      const data = await response.json();
      setBusinessNames(data.businessNames);
      toast.success('Business names generated successfully!');
    } catch (error) {
      console.error('Error generating business names:', error);
      toast.error('Failed to generate business names. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectName = (name: BusinessName) => {
    setSelectedName(name);
    updateBrandKit({
      businessName: name.name,
      tagline: name.tagline,
      industry,
      description,
    });
    toast.success(`Selected: ${name.name}`);
  };

  const handleContinue = () => {
    if (!selectedName) {
      toast.error('Please select a business name to continue');
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 1: Generate Your Business Name</h2>
        <p className="text-gray-600">
          Tell us about your business and we'll generate unique, memorable names for you.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g., Technology, Healthcare, E-commerce"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            required
          />
        </div>

        {/* Keywords */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Keywords <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add keywords (press Enter)"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              className="px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {keywords.map(keyword => (
              <span
                key={keyword}
                className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
              >
                {keyword}
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(keyword)}
                  className="hover:text-orange-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what your business does..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            required
          />
        </div>

        {/* Generate Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Generating...' : 'Generate Business Names'}
        </button>
      </form>

      {/* Results */}
      {businessNames.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900">Generated Names</h3>
          <div className="grid gap-4">
            {businessNames.map((name, index) => (
              <div
                key={index}
                className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                  selectedName?.name === name.name
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
                onClick={() => handleSelectName(name)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">{name.name}</h4>
                    <p className="text-sm text-gray-600 mb-2 italic">"{name.tagline}"</p>
                    <p className="text-sm text-gray-700">{name.explanation}</p>
                  </div>
                  {selectedName?.name === name.name && (
                    <div className="ml-4">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Continue Button */}
          {selectedName && (
            <button
              onClick={handleContinue}
              className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
              Continue to Logo Design
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessNameTab;
